import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import routes from '@/router/routes';
import './index.scss';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedKeys, setExpandedKeys] = useState([]);

  const menuItems = useMemo(() => {
    // 查找被 RouteGuard 包裹并且包含 children 的路由
    const guarded = routes.find((r) => r?.children && Array.isArray(r.children));
    const children = (guarded?.children || []).filter((child) => !child.hide);

    // 按路由表顺序构建菜单，保留分组首次出现的位置
    const result = [];
    const groupMap = {};

    children.forEach((child) => {
      if (child.groupKey) {
        if (!groupMap[child.groupKey]) {
          // 直接用 groupKey 首字母大写作为分组标题
          const groupTitle = child.groupKey.charAt(0).toUpperCase() + child.groupKey.slice(1);
          const groupItem = {
            key: `/${child.groupKey}`,
            icon: null,
            title: groupTitle,
            children: [],
          };
          groupMap[child.groupKey] = groupItem;
          // 占位插入 result，保留路由表中的顺序
          result.push(groupItem);
        }
        // 第一个有 icon 的子路由作为分组的 icon
        if (child.icon && !groupMap[child.groupKey].icon) {
          groupMap[child.groupKey].icon = child.icon;
        }
        groupMap[child.groupKey].children.push({
          key: `/${child.path}`,
          path: `/${child.path}`,
          title: child.title || '',
        });
      } else {
        const isIndex = Boolean(child.index);
        const path = isIndex ? '/' : `/${child.path}`;
        const title = child.title || '';
        const icon = child.icon || <FileOutlined />;

        result.push({ key: path, path, icon, title });
      }
    });

    // 补全没有 icon 的分组
    result.forEach((item) => {
      if (item.children && !item.icon) {
        item.icon = <FileOutlined />;
      }
    });

    return result;
  }, []);

  // 自动展开当前路由对应的父级菜单
  useEffect(() => {
    const guarded = routes.find((r) => r?.children && Array.isArray(r.children));
    const allChildren = guarded?.children || [];

    menuItems.forEach((item) => {
      if (item.children) {
        // 检查是否有子菜单项匹配当前路径
        const hasActiveChild = item.children.some((subItem) => {
          // 直接匹配
          if (location.pathname === subItem.path) return true;

          // 检查是否有 detail 路由指向这个子菜单项
          const detailRoute = allChildren.find((child) => child.parentPath === subItem.path.replace('/', '') && location.pathname === `/${child.path}`);
          return Boolean(detailRoute);
        });

        if (hasActiveChild && !expandedKeys.includes(item.key)) {
          setExpandedKeys((prev) => [...prev, item.key]);
        }
      }
    });
  }, [location.pathname, menuItems]);

  const handleNavigate = useCallback(
    (path) => {
      // return;
      if (path && path !== location.pathname) {
        navigate(path);
      }
    },
    [location.pathname, navigate]
  );

  const toggleExpand = useCallback((key) => {
    setExpandedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  return (
    <aside className="sidebar">
      <div className="sidebarHeader">
        <span className="logoIcon" aria-hidden="true" />
        <span className="logoText">Backoffice</span>
      </div>

      <ul className="menuList">
        {menuItems.map((item) => {
          // 检查是否有子菜单
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = expandedKeys.includes(item.key);

          // 只有没有子菜单的一级菜单才需要判断激活状态
          let isActive = false;
          if (!hasChildren) {
            isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path + '/'));

            // 检查是否有 parentPath 指向当前菜单项（处理 detail 路由的激活状态）
            if (!isActive) {
              const guarded = routes.find((r) => r?.children && Array.isArray(r.children));
              const allChildren = guarded?.children || [];
              const detailRoute = allChildren.find((child) => child.parentPath === item.path.replace('/', '') && location.pathname === `/${child.path}`);
              isActive = Boolean(detailRoute);
            }
          }

          return (
            <li key={item.key}>
              <div className={`menuItem${isActive ? ' active' : ''}`} onClick={() => (hasChildren ? toggleExpand(item.key) : handleNavigate(item.path))}>
                <span className="menuIcon" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="menuText">{item.title}</span>
                {hasChildren && <span className="menuArrow">{isExpanded ? <DownOutlined /> : <RightOutlined />}</span>}
              </div>
              {hasChildren && isExpanded && (
                <ul className="subMenuList">
                  {item.children.map((subItem) => {
                    // 检查当前路径是否匹配子菜单项
                    let isSubActive = location.pathname === subItem.path;

                    // 检查是否有 detail 路由的 parentPath 指向当前子菜单项
                    if (!isSubActive) {
                      const guarded = routes.find((r) => r?.children && Array.isArray(r.children));
                      const allChildren = guarded?.children || [];
                      const detailRoute = allChildren.find((child) => child.parentPath === subItem.path.replace('/', '') && location.pathname === `/${child.path}`);
                      isSubActive = Boolean(detailRoute);
                    }

                    return (
                      <li
                        key={subItem.key}
                        className={`subMenuItem${isSubActive ? ' active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigate(subItem.path);
                        }}
                      >
                        <span className="subMenuText">{subItem.title}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      <div className="sidebarFooter">
        <span className="versionText">Version: 1.0.0</span>
      </div>
    </aside>
  );
};

export default Sidebar;
